package cc.bluepeak.clubmanager.site.styles

import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.forms.InputStyle
import com.varabyte.kobweb.silk.components.forms.InputVars
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.selectors.placeholder
import com.varabyte.kobweb.silk.style.selectors.selection
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.link
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import com.varabyte.kobweb.silk.theme.modifyStyle
import kotlinx.browser.document
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px

val scrollbarColor by StyleVariable<Color>()
val selectionColor by StyleVariable<Color>()
val backgroundColor by StyleVariable<Color>()
val placeholderColor by StyleVariable<Color>()

const val COLOR_MODE_KEY = "color-mode"

@InitSilk
fun initStyleSheet(ctx: InitSilkContext) {
    ctx.theme.palettes.apply {
        light.apply {
            background = Theme.lightBackground
            color = Theme.black
            link.apply {
                default = Theme.primary
                visited = Theme.primary
            }
        }
        dark.apply {
            background = Theme.darkBackground
            color = Theme.white
            link.apply {
                default = Theme.primary
                visited = Theme.primary
            }
        }
    }

    ctx.config.initialColorMode = localStorage.getItem(COLOR_MODE_KEY)?.let { ColorMode.valueOf(it) }
        ?: ColorMode.systemPreference

    if (AppGlobals.isExporting) {
        document.head!!.appendChild(
            document.createElement("script").apply {
                textContent = $$"""
            {
               const storedColor = localStorage.getItem('$${COLOR_MODE_KEY}'); // 'LIGHT', 'DARK', or null
               const desiredColor = storedColor
                  ? `silk-${storedColor.toLowerCase()}`
                  : (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'silk-dark' : 'silk-light');
               const oppositeColor = desiredColor === 'silk-dark' ? 'silk-light' : 'silk-dark';
               document.documentElement.classList.replace(oppositeColor, desiredColor);
            }
         """.trimIndent()
            }
        )
    }
    ctx.stylesheet.registerStyleBase("html") {
        Modifier.backgroundColor(backgroundColor.value(Theme.lightBackground))
    }
    ctx.stylesheet.registerStyleBase("body") {
        Modifier.fontFamily("Urbanist")
            .fontSize(16.px)
            .lineHeight(1.5).styleModifier {
                property("-webkit-touch-callout", "none !important")
            }
    }
    ctx.stylesheet.registerStyle("*") {
        cssRule("::-webkit-scrollbar") {
            Modifier
                .width(8.px)
                .height(8.px)
                .backgroundColor(Colors.Transparent)
        }
        cssRule("::-webkit-scrollbar-thumb") {
            Modifier
                .borderRadius(10.px)
                .backgroundColor(scrollbarColor.value(Theme.primary))
        }
        cssRule("::-webkit-scrollbar-corner") {
            Modifier
                .backgroundColor(Colors.Transparent)
        }
        selection {
            Modifier.backgroundColor(selectionColor.value(Theme.primary)).color(Theme.white)
        }
    }
    ctx.stylesheet.registerStyleBase("h1") {
        Modifier
            .fontSize(2.em)
            .fontWeight(700)
            .margin(0.px)
            .whiteSpace(WhiteSpace.BreakSpaces)
    }
    ctx.stylesheet.registerStyleBase("h2") {
        Modifier
            .fontSize(1.5.em)
            .fontWeight(600)
            .margin(0.px)
            .whiteSpace(WhiteSpace.BreakSpaces)
    }
    ctx.stylesheet.registerStyleBase("h3") {
        Modifier
            .fontSize(1.2.em)
            .fontWeight(600)
            .margin(0.px)
            .whiteSpace(WhiteSpace.BreakSpaces)
    }
    ctx.stylesheet.registerStyleBase("p") {
        Modifier
            .margin(0.px)
            .whiteSpace(WhiteSpace.BreakSpaces)
    }
    ctx.theme.modifyStyle(ButtonStyle) {
        val sitePalette = colorMode.toSitePalette()
        base {
            Modifier
                .borderRadius(8.px)
                .fontSize(1.cssRem)
                .setVariable(ButtonVars.BackgroundDefaultColor, sitePalette.primary)
                .setVariable(ButtonVars.BackgroundPressedColor, sitePalette.primary.lightened())
                .setVariable(ButtonVars.BackgroundFocusColor, sitePalette.primary.lightened())
                .setVariable(ButtonVars.BackgroundHoverColor, sitePalette.primary.lightened())
                .setVariable(ButtonVars.Color, Theme.white)
                .boxShadow(
                    0.px,
                    4.px,
                    blurRadius = 8.px,
                    color = ButtonVars.BackgroundDefaultColor.value(sitePalette.primary)
                )
                .fontFamily("Urbanist")
                .whiteSpace(WhiteSpace.BreakSpaces)
        }
    }
    ctx.theme.modifyStyle(InputStyle) {
        base {
            Modifier
                .color(colorMode.toPalette().color)
                .setVariable(InputVars.BorderFocusColor, Theme.primary)
                .fontFamily("Urbanist")
        }
        placeholder {
            Modifier.color(placeholderColor.value(Theme.secondary))
        }
    }
}